import React, {useCallback, useEffect, useMemo, useState} from "react";
import {Tab, Tabs} from "../../../../../../components/base/Tabs";
import {createUUId, formatMoney, numComparator, stringComparator} from "../../../../../../../core/services/utils/utils";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnTypes, DataGridExoticColumnFields} from "../../../../../../../packages/data-grid";
import {BulkInventoryAdjustmentProductSelectionTableCellKeys} from "../../../../../../../core/constants/table/data-grid-column-keys";
import IconButton from "../../../../../../components/base/button/icon_button";
import {ArrowBack, ChevronRightRounded, Edit} from "@mui/icons-material";
import classnames from "classnames";
import Chips, {ChipsContentType, ChipsTypes} from "../../../../../../components/base/chips/chips";
import SimpleMoreDetailsDialog from "../../../../../../components/dialogs/simple-more-details-dialog";
import SearchInput from "../../../../../../components/base/input/search_input";
import PartnerDataGrid, {useDataGridSavedState} from "../../../../../../containers/data-grid";
import {OutlinedInput} from "../../../../../../components/base/input/outlined_input";
import UpsertBulkAdjustmentProduct from "../../../../../../components/dialogs/upsert-bulk-adjustment-product";
import ModifyProductAvailabilityDialog from "../../../../../../components/dialogs/modify-product-availability";
import Button from "../../../../../../components/base/button/button";
import {Col} from "reactstrap";
import SummaryInfoCard from "../../../../../../components/app-specific/summary-cards/summary-info-card";
import {Paper, SvgIcon} from "@mui/material";
import {ReactComponent as RemoveIcon} from "../../../../../../../assets/images/remove.svg";
import Api from "../../../../../../../core/services/api/api";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import {routeFunctions} from "../../../../../../../core/models/static/routes/app-routes";
import {useConfigureNotebook} from "../../../../../../hooks/use-notebook-config";
import {PanelFooter} from "../../../../../../components/layout";
import ProductInformationPopover from "../../../../../../components/app-specific/draggable-popover/product-information-popover";
import {useHistory} from "react-router-dom";
import useBehavioralAnalyticsTool from "../../../../../../hooks/use-behavioural-analytics-tool";
import PartAutocomplete from "../../../../../../components/text-fields/autocomplete/part-autocomplete";


const BulkInventoryAdjustmentTabs = {
    add: 'add',
    remove: 'remove',
}


const BulkInventoryAdjustmentStatus = {
    partSelection: 'partSelection',
    review: 'review',
}

const _initialPaginationInfo = {
    pageSize: 10,
    currentPage: 1,
    length: 0,
    sizes: [5, 10, 15],
};

const initAvailabilityModificationDialogState = {
    open: false,
    warehouse: null,
    currentAvailability: 0,
    partNo: '',
    selectedSerials: [],
};

const adjustmentBasicInfoBodyIds = {
    selectedPart: 'selectedParts',
    totalPart: 'totalPart',
    type: 'type',
}

const dataGridSaveConfig = {
    name: 'BulkAdjustInventoryViewDataGrid',
    version: 1,
}

const BulkAdjustInventoryView = () => {
    const history = useHistory();
    const [submitting, setSubmitting] = useState(false);
    const [allSelectedProducts, setAllSelectedProducts] = useState([]);
    const [bulkInventoryAdjustmentStatus, setBulkInventoryAdjustmentStatus] = useState(BulkInventoryAdjustmentStatus.partSelection);
    const [fullNoteDialogOpen, setFullNoteDialogOpen] = useState(false);
    const [fullNote, setFullNote] = useState(null);
    const [keywords, setKeywords] = useState('');
    const [additionMoreInfoDialogOpen, setAdditionMoreInfoDialogOpen] = useState(false)
    const [selectedPart, setSelectedPart] = useState(null);
    const [upsertingWarehouseReasonNoteInitialData, setUpsertingWarehouseReasonNoteInitialData] = useState(null);
    const [availabilityModificationDialogState, setAvailabilityModificationDialogState] = useState(initAvailabilityModificationDialogState);
    const [processAdditionalInfo, setProcessAdditionalInfo] = useState("");
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('bulk-inventory-adjustment-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    const [selectedTab, setSelectedTab] = useState(BulkInventoryAdjustmentTabs.add);
    const tabs = Object.values(BulkInventoryAdjustmentTabs).map(e => ({
        value: e,
        name: (function () {
            switch (e) {
                case BulkInventoryAdjustmentTabs.add:
                    return 'Add';
                case BulkInventoryAdjustmentTabs.remove:
                    return 'Remove';
                default:
                    return '';
            }
        })(),
    }));


    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral analytics tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.inventoryAdjustmentBulkAvailabilityChange);
        configureBehavioralTool((tags) => tags.inventoryAdjustmentBulkAvailabilityChange);
    }, [configureBehavioralTool, configureNotebookTags]);

    /**
     * Memo version of columns entities used in data grid
     * @type {DataGridColumn[]}
     */
    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            {
                name: BulkInventoryAdjustmentProductSelectionTableCellKeys.actionButton,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: false,
                sortable: false,
                resizable: false,
                visibilityToggleable: false,
                width: 0,
            },
            {
                name: DataGridExoticColumnFields.detailedPanelToggler,
            },
            {
                title: "Part Number",
                name: BulkInventoryAdjustmentProductSelectionTableCellKeys.partNumber,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 100,
            },
            {
                title: "Category",
                name: BulkInventoryAdjustmentProductSelectionTableCellKeys.category,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 100,
            },
            {
                title: "Current Availability",
                name: BulkInventoryAdjustmentProductSelectionTableCellKeys.currentAvailability,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 140,
            },
            {
                title: selectedTab === BulkInventoryAdjustmentTabs.add ? "Added Quantity" : 'Removed Quantity',
                name: BulkInventoryAdjustmentProductSelectionTableCellKeys.quantity,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: 150,
            },
            {
                title: 'Cost',
                name: BulkInventoryAdjustmentProductSelectionTableCellKeys.cost,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                width: 60,
            },
            {
                title: 'Extended Cost',
                name: BulkInventoryAdjustmentProductSelectionTableCellKeys.extendedCost,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 110,
            },
            {
                title: 'Average Cost',
                name: BulkInventoryAdjustmentProductSelectionTableCellKeys.averageCost,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 100,
            },
            {
                title: 'Min Price',
                name: BulkInventoryAdjustmentProductSelectionTableCellKeys.minPrice,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                width: 100,
            },
            {
                title: 'Warehouse',
                name: BulkInventoryAdjustmentProductSelectionTableCellKeys.warehouse,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.element,
                width: 120,
            },
            {
                title: 'Reason Code',
                name: BulkInventoryAdjustmentProductSelectionTableCellKeys.reasonCode,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.element,
                width: 100,
            },
            {
                title: 'Note',
                name: BulkInventoryAdjustmentProductSelectionTableCellKeys.note,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.element,
                width: 400,
                sortable: false,
            },
            {
                name: DataGridExoticColumnFields.detailedPanel,
            }
        ], [selectedTab])

    const [dataGridColumns, dataGridSortBy, dataGridPageSize, dataGridDensities] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
        sortByObjectMap: {
            property: "descending",
            descending: "field",
        },
    });

    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ..._initialPaginationInfo,
        pageSize: dataGridPageSize
    } : _initialPaginationInfo, [dataGridPageSize]);

    const [paginationInfo, setPaginationInfo] = useState(initialPaginationInfo);
    const [orderBy, setOrderBy] = useState(dataGridSortBy);

    /**
     * Width of the serial numbers wrapper in the expandable section of data grid
     * @type {string}
     */
    const expandableSectionWidth = useMemo(() => {
        const dataGridHeader = document.getElementsByClassName('data-grid-toolbar')?.[0];
        return `${dataGridHeader?.offsetWidth}px` ?? '0';
    }, [submitting, allSelectedProducts])

    /**@type {function(a, b): number}*/
    const orderByMethod = useMemo(() => {
        const getExtendedCost = (item) => {
            const multiplier = ((selectedTab === BulkInventoryAdjustmentTabs.remove && item.quantity !== 0) ? -1 : 1);
            return item.isSerialized
                ? multiplier * item.extendedCost
                : multiplier * item?.quantity * (Number.isNaN(Number(item?.cost)) ? 0 : Number(item?.cost))
        }
        switch (orderBy?.field) {
            case BulkInventoryAdjustmentProductSelectionTableCellKeys.partNumber:
                return orderBy?.descending
                    ? (a, b) => stringComparator(a.partNumber, b.partNumber)
                    : (a, b) => stringComparator(b.partNumber, a.partNumber)
            case BulkInventoryAdjustmentProductSelectionTableCellKeys.category:
                return orderBy?.descending
                    ? (a, b) => stringComparator(a.category, b.category)
                    : (a, b) => stringComparator(b.category, a.category)
            case BulkInventoryAdjustmentProductSelectionTableCellKeys.quantity:
                return orderBy?.descending
                    ? (a, b) => numComparator(Number(a.quantity ?? '0'), Number(b.quantity ?? '0'))
                    : (a, b) => numComparator(Number(b.quantity ?? '0'), Number(a.quantity ?? '0'))
            case BulkInventoryAdjustmentProductSelectionTableCellKeys.cost:
                return orderBy?.descending
                    ? (a, b) => numComparator(Number(a.cost ?? '0'), Number(b.cost ?? '0'))
                    : (a, b) => numComparator(Number(b.cost ?? '0'), Number(a.cost ?? '0'))
            case BulkInventoryAdjustmentProductSelectionTableCellKeys.currentAvailability:
                return orderBy?.descending
                    ? (a, b) => numComparator(Number(a.currentAvailability), Number(b.currentAvailability))
                    : (a, b) => numComparator(Number(b.currentAvailability), Number(a.currentAvailability))
            case BulkInventoryAdjustmentProductSelectionTableCellKeys.averageCost:
                return orderBy?.descending
                    ? (a, b) => numComparator(Number(a.averageCost), Number(b.averageCost))
                    : (a, b) => numComparator(Number(b.averageCost), Number(a.averageCost))
            case BulkInventoryAdjustmentProductSelectionTableCellKeys.extendedCost:
                return orderBy?.descending
                    ? (a, b) => {
                        const aExtendedCost = getExtendedCost(a);
                        const bExtendedCost = getExtendedCost(b);
                        return numComparator(Number(aExtendedCost), Number(bExtendedCost))
                    }
                    : (a, b) => {
                        const aExtendedCost = getExtendedCost(a);
                        const bExtendedCost = getExtendedCost(b);
                        return numComparator(Number(bExtendedCost), Number(aExtendedCost))
                    }
            case BulkInventoryAdjustmentProductSelectionTableCellKeys.minPrice:
                return orderBy?.descending
                    ? (a, b) => numComparator(Number(a.minPrice), Number(b.minPrice))
                    : (a, b) => numComparator(Number(b.minPrice), Number(a.minPrice))
            case BulkInventoryAdjustmentProductSelectionTableCellKeys.warehouse:
                return orderBy?.descending
                    ? (a, b) => stringComparator(a.warehouse?.name, b.warehouse?.name)
                    : (a, b) => stringComparator(b.warehouse?.name, a.warehouse?.name)
            case BulkInventoryAdjustmentProductSelectionTableCellKeys.reasonCode:
                return orderBy?.descending
                    ? (a, b) => stringComparator(a.reason?.code, b.reason?.code)
                    : (a, b) => stringComparator(b.reason?.code, a.reason?.code)
            default:
                return () => 0
        }
    }, [orderBy?.field, orderBy?.descending, selectedTab]);

    /**
     * As soon as the tab change is triggered:
     * Shows a confirmation dialog to user regarding
     */
    const onTabChanged = (_, newValue) => {
        if (allSelectedProducts.length > 0) {
            window.partner?.openConfirmationDialog(
                <div className={'p-3'}>
                    <p className={'text-sm text-color-2'}>
                        {`Do you want to change the bulk inventory adjustment process from ${newValue === BulkInventoryAdjustmentTabs.add ? 'Remove' : 'Add'} to ${newValue === BulkInventoryAdjustmentTabs.add ? 'Add' : 'Remove'}. By doing so, some fields values would be changed`}
                    </p>
                </div>,
                {
                    maxWidth: 'sm',
                },
                (confirmed) => confirmed && onTabChangeConfirmed(newValue),
            );
            return;
        }
        onTabChangeConfirmed(newValue);
    }

    /**
     * Sets the tab value to the newly selected tab after confirmation
     * @param newValue
     */
    const onTabChangeConfirmed = (newValue) => {
        setSelectedTab(newValue);
        setAllSelectedProducts(prevState => prevState.map(e => ({
            ...e,
            selectedSerials: [],
            quantity: 0,
            cost: e.defaultCost,
            extendedCost: 0
        })))
    }

    /**
     * As soon as the product is selected:
     * - Opens a dialog for getting extra information from user such as warehouse and reason
     * @param part
     */
    const onPartSelected = (part) => {
        setSelectedPart(part);
        setAdditionMoreInfoDialogOpen(true);
    }

    /**
     * Handles adding product to the bulk inventory adjustment
     * @param values
     */
    const onProductAdded = async (values) => {
        await handleAddingPart(selectedPart, values);
        setAdditionMoreInfoDialogOpen(false);
        setSelectedPart(null);
    }

    /**
     * removes the selected product from bulk inventory adjustment
     * @param item
     */
    const removeProductFromBulk = (item) => {
        setAllSelectedProducts(prevState => prevState.filter(e => e.id !== item.id))
    }

    /**
     * Adds the selected product to the list of bulk inventory adjustment selected parts
     * @param part
     * @param moreInfo
     */
    const handleAddingPart = async (part, moreInfo) => {
        const response = await Api.getProductKeyInformation(
            part?.partNo ?? part?.partNumber,
            moreInfo.warehouse?.code,
        )
        if (!isMounted())
            return;
        if (!response?.resultFlag || !response?.data)
            return
        const productKeyInfo = response.data;
        setAllSelectedProducts(prevState => [
            ...prevState,
            {
                id: createUUId(),
                part: part,
                partId: productKeyInfo.id,
                partNumber: productKeyInfo.partNo,
                category: productKeyInfo.category,
                parentCategoryId: productKeyInfo.parentCategoryId,
                parentCategoryTitle: productKeyInfo.parentCategory,
                cost: productKeyInfo.currentCost,
                defaultCost: productKeyInfo.currentCost,
                averageCost: productKeyInfo.averageCost,
                extendedCost: 0,
                minPrice: productKeyInfo.minimumPrice,
                quantity: 0,
                currentAvailability: productKeyInfo.availableQty,
                reason: moreInfo?.reason,
                note: moreInfo?.note,
                warehouse: moreInfo?.warehouse,
                isSerialized: productKeyInfo.isSerializable,
                selectedSerials: [],
            }
        ])
    }

    /**
     * Opens a dialog for showing the note full details
     * @param note
     */
    const viewFullNote = (note) => {
        setFullNote(note);
        setFullNoteDialogOpen(true);
    }

    /**
     * Opens the related dialog for modifying the quantity of serialized products
     * @param item
     */
    const handleUpsertingQuantity = (item) => {
        setSelectedPart(item);
        setAvailabilityModificationDialogState({
            warehouse: item.warehouse,
            partNo: item?.partNumber ?? '',
            open: true,
            currentAvailability: item?.currentAvailability,
            selectedSerials: item?.selectedSerials ?? [],
            cost: item?.cost,
        })
    }

    /**
     * Updates the product quantity, cost, and serial numbers
     * @param isIncrease
     * @param serialNumbers
     * @param cost
     */
    const handleUpdatingSerializedPartSerials = (isIncrease, serialNumbers, cost) => {
        let count = serialNumbers.length;
        setAllSelectedProducts(prevState => prevState.map(e => e.id === selectedPart.id ? {
            ...e,
            selectedSerials: serialNumbers,
            quantity: count,
            cost: cost,
            extendedCost: isIncrease ? count * cost : serialNumbers.reduce((prev, cur) => prev + (cur?.cost ?? 0), 0)
        } : e))
    }

    /**
     * Removes a serial number from the list of serial numbers of a product
     * Also updates the current quantity, cost, and extended cost of the product in the list
     * @param item
     * @param serialNumber
     */
    const removeSerialNumber = (item, serialNumber) => {
        const isAdding = selectedTab === BulkInventoryAdjustmentTabs.add;
        setAllSelectedProducts(prevState => prevState.map((e) => {
                if (e.id === item.id) {
                    const _filteredSerials = e.selectedSerials.filter(t => isAdding ? t !== serialNumber : t.serialNo !== serialNumber.serialNo);
                    const isIncrease = selectedTab === BulkInventoryAdjustmentTabs.add;
                    return {
                        ...e,
                        selectedSerials: _filteredSerials,
                        quantity: _filteredSerials?.length ?? 0,
                        extendedCost: isIncrease
                            ? (_filteredSerials?.length ?? 0) * e.cost
                            : _filteredSerials.reduce((prev, cur) => {
                                return prev + (cur?.cost ?? 0)
                            }, 0)
                    }
                }
                return e;
            }
        ))
    }

    /**
     * Pops up upserting warehouse, reason, and note dialog and sets the dialog's initial data
     * @param item
     */
    const handleUpsertingWarehouseReasonAndNote = (item) => {
        setSelectedPart(item.part);
        setUpsertingWarehouseReasonNoteInitialData({
            warehouse: item?.warehouse,
            reason: item?.reason,
            note: item?.note,
        })
        setAdditionMoreInfoDialogOpen(true);
    }

    /**
     * Updates the record warehouse, note, and reason based on the values received from the upsertion dialog
     * @param data
     */
    const updateProductWarehouseReasonNote = (data) => {
        setAllSelectedProducts(prevState => prevState.map(e => e.partId === selectedPart.id ? {...e, ...data} : e))
        setSelectedPart(null);
        setUpsertingWarehouseReasonNoteInitialData(null);
        setAdditionMoreInfoDialogOpen(false);
    }

    /**
     * Changes the value of add/remove quantity field for product:
     * if the input value is invalid, updates quantity to empty string
     * otherwise, sets it with the parse value
     * @param item
     * @param value
     */
    const handleUnSerializedCountChanged = (item, value) => {
        const parsedValue = parseFloat(value);
        let parsedPrice;
        if (isNaN(parsedValue)) {
            setAllSelectedProducts(prevState => prevState.map(e => e.id === item.id ? {...e, quantity: ''} : e))
            return;
        } else if (parsedValue < 0) {
            parsedPrice = -1 * parsedValue;
        } else if (parsedValue > item.currentAvailability && selectedTab === BulkInventoryAdjustmentTabs.remove) {
            parsedPrice = item.currentAvailability;
        } else {
            parsedPrice = parsedValue;
        }
        setAllSelectedProducts(prevState => prevState.map(e => e.id === item.id ? {...e, quantity: parsedPrice} : e))
    }

    /**
     * Sets the serial number cost in the list of entries with its previous value if data was invalid when text field was blurred
     * Otherwise, updates the cost of the selected serial number in the selectedSerialNos list
     * @param e
     * @param item
     */
    const handleUnSerializedCountBlurred = (e, item) => {
        const parsedValue = parseFloat(e.target.value);
        if (isNaN(parsedValue)) {
            setAllSelectedProducts(prevState => prevState.map(e => e.id === item.id
                ? {...e, quantity: 0}
                : e
            ))
            return;
        }
        setAllSelectedProducts(prevState => prevState.map(e => e.id === item.id ? {...e, quantity: parsedValue} : e));
    }

    /**
     * Changes the value of cost field for an un-serialized product:
     * if the input value is invalid, updates the cost in entries list with empty string
     * otherwise, sets it with the parsed value
     * @param item
     * @param value
     */
    const handleUnSerializedCostChange = (item, value) => {
        const parsedValue = parseFloat(value);
        let parsedPrice;
        if (isNaN(parsedValue)) {
            setAllSelectedProducts(prevState => prevState.map(e => e.id === item.id ? {...e, cost: ''} : e))
            return;
        } else if (parsedValue < 0) {
            parsedPrice = -1 * parsedValue;
        } else {
            parsedPrice = parsedValue;
        }
        setAllSelectedProducts(prevState => prevState.map(e => e.id === item.id ? {...e, cost: parsedPrice} : e))
    }

    /**
     * Sets the serial number cost in the list of entries with its previous value if data was invalid when text field was blurred
     * Otherwise, updates the cost of the selected serial number in the selectedSerialNos list
     * @param e
     * @param item
     */
    const handleUnSerializedCostFieldBlurred = (e, item) => {
        const parsedValue = parseFloat(e.target.value);
        if (isNaN(parsedValue)) {
            setAllSelectedProducts(prevState => prevState.map(e => e.id === item.id
                ? {...e, cost: e.defaultCost}
                : e
            ))
            return;
        }
        setAllSelectedProducts(prevState => prevState.map(e => e.id === item.id ? {...e, cost: parsedValue} : e));
    }

    /**
     * Memo version of visible records in the table
     * @type {*[]}
     */
    const visibleRecords = useMemo(() => {
        const normalizedKeyword = keywords?.toLowerCase()?.trim() ?? '';
        const entities = Array
            .from(allSelectedProducts ?? [])
            .filter(e => e.partNumber?.toLowerCase()?.trim()?.includes(normalizedKeyword))
            .sort((a, b) => orderByMethod(a, b));
        const start = Math.min(Math.max((paginationInfo.currentPage - 1) * paginationInfo.pageSize, 0), allSelectedProducts.length);
        const end = Math.min((paginationInfo.currentPage) * paginationInfo.pageSize, allSelectedProducts.length);
        return entities.slice(start, end);
    }, [keywords, allSelectedProducts, paginationInfo.currentPage, paginationInfo.pageSize, orderByMethod])

    /**
     * Sorts the entries of the credits in place.
     * @param {DataGridSortBy} tableOrderBy
     */
    const onSort = useCallback((tableOrderBy) => {
        setOrderBy(tableOrderBy);
        setPaginationInfo(prevState => ({...prevState, currentPage: 1}));
    }, [])

    /**
     * Creates an inventory adjustment with the selected products
     * @type {(function(): Promise<void>)|*}
     */
    const createInventoryAdjustment = useCallback(async () => {
        const forApi = {
            items: allSelectedProducts
                .map(item => {
                    if (item.isSerialized) {
                        return item.selectedSerials?.map(e => ({
                            partNumber: item.partNumber,
                            partId: item.partId,
                            parentCategoryId: item.parentCategoryId,
                            parentCategoryTitle: item.parentCategoryTitle,
                            serialNumber: e?.serialNo ?? e,
                            cost: e?.cost ?? item.cost,
                            quantity: 1,
                            warehouseCode: item?.warehouse?.code,
                            reasonId: item?.reason?.id ?? null,
                            note: item?.note ?? null,
                        })) ?? [];
                    }
                    return [{
                        partNumber: item.partNumber,
                        partId: item.partId,
                        parentCategoryId: item.parentCategoryId,
                        parentCategoryTitle: item.parentCategoryTitle,
                        serialNumber: null,
                        cost: item.cost,
                        quantity: Number(item.quantity),
                        warehouseCode: item?.warehouse?.code,
                        reasonId: item?.reason?.id ?? null,
                        note: item?.note ?? null,
                    }];
                })
                .flat()
                .filter(e => e.quantity > 0),
            isAdd: selectedTab === BulkInventoryAdjustmentTabs.add,
            description: processAdditionalInfo,
        }
        setSubmitting(true);
        const response = await Api.createInventoryAdjustment(forApi);
        if (!isMounted())
            return;
        setSubmitting(false);
        if (response?.resultFlag) {
            history.replace(routeFunctions.inventory.inventoryAdjustment.single(response.data.id));
        }
    }, [allSelectedProducts, selectedTab, processAdditionalInfo, isMounted, history]);


    /**
     * Memo version of data grid Rows
     * @type {DataGridRow[]}
     */
    const dataGridRows = useMemo(() => {
        return visibleRecords?.map((item) => ({
            key: item.id,
            data: item,
            cells: {
                [BulkInventoryAdjustmentProductSelectionTableCellKeys.actionButton]:
                    <IconButton
                        color={'paper.error'}
                        onPressed={() => removeProductFromBulk(item)}
                    >
                        <SvgIcon component={RemoveIcon} className='icon normal'/>
                    </IconButton>,
                [DataGridExoticColumnFields.detailedPanelToggler]: null,
                [BulkInventoryAdjustmentProductSelectionTableCellKeys.partNumber]:
                    <ProductInformationPopover partNo={item?.partNumber}>
                        {item?.partNumber ?? '--'}
                    </ProductInformationPopover>,
                [BulkInventoryAdjustmentProductSelectionTableCellKeys.category]: item?.category,
                [BulkInventoryAdjustmentProductSelectionTableCellKeys.currentAvailability]: item?.currentAvailability,
                [BulkInventoryAdjustmentProductSelectionTableCellKeys.quantity]: item?.isSerialized
                    ? <div className={'d-flex align-items-center justify-content-center'}>
                        <IconButton
                            onPressed={() => handleUpsertingQuantity(item)}
                            disabled={submitting}
                            color={'secondary'}
                            className={'mr-2'}
                        >
                            <Edit className={"icon small"}/>
                        </IconButton>
                        <p {...dataGridCellTextProps}>{item?.quantity}</p>
                    </div>
                    : <div className={'w-50'}>
                        <UnSerializedQuantityField
                            value={item?.quantity}
                            handleQuantityChanged={(value) => handleUnSerializedCountChanged(item, value)}
                            handleFieldBlurred={(e) => handleUnSerializedCountBlurred(e, item)}
                        />
                    </div>,
                [BulkInventoryAdjustmentProductSelectionTableCellKeys.cost]: item?.isSerialized
                    ? null
                    : <div className={'w-90'}>
                        <UnSerializedCostField
                            value={item?.cost}
                            handleCostChange={(value) => handleUnSerializedCostChange(item, value)}
                            onFieldBlurred={(e) => handleUnSerializedCostFieldBlurred(e, item)}
                        />
                    </div>,
                [BulkInventoryAdjustmentProductSelectionTableCellKeys.extendedCost]: item.isSerialized
                    ? ((selectedTab === BulkInventoryAdjustmentTabs.remove && item.quantity !== 0) ? -1 : 1) * item.extendedCost
                    : ((selectedTab === BulkInventoryAdjustmentTabs.remove && item.quantity !== 0) ? -1 : 1)
                    * item?.quantity
                    * (Number.isNaN(Number(item?.cost)) ? 0 : Number(item?.cost)),
                [BulkInventoryAdjustmentProductSelectionTableCellKeys.averageCost]: item?.averageCost,
                [BulkInventoryAdjustmentProductSelectionTableCellKeys.minPrice]: item?.minPrice,
                [BulkInventoryAdjustmentProductSelectionTableCellKeys.warehouse]: <div
                    className={'d-flex align-items-center justify-content-center'}>
                    <IconButton
                        onPressed={() => handleUpsertingWarehouseReasonAndNote(item)}
                        disabled={submitting}
                        color={'secondary'}
                        className={'mr-2'}
                    >
                        <Edit className={"icon small"}/>
                    </IconButton>
                    <p {...dataGridCellTextProps}>{item?.warehouse?.name ?? ''}</p>
                </div>,
                [BulkInventoryAdjustmentProductSelectionTableCellKeys.reasonCode]:
                    <div className={'d-flex align-items-center justify-content-center'}>
                        <IconButton
                            onPressed={() => handleUpsertingWarehouseReasonAndNote(item)}
                            disabled={submitting}
                            color={'secondary'}
                            className={'mr-2'}
                        >
                            <Edit className={"icon small"}/>
                        </IconButton>
                        <p {...dataGridCellTextProps}>{item?.reason?.code ?? ''}</p>
                    </div>,
                [BulkInventoryAdjustmentProductSelectionTableCellKeys.note]: item?.note?.length > 30
                    ? <div className={'note-container'}>
                        <IconButton
                            onPressed={() => handleUpsertingWarehouseReasonAndNote(item)}
                            disabled={submitting}
                            color={'secondary'}
                            className={'mr-2'}
                        >
                            <Edit className={"icon small"}/>
                        </IconButton>
                        <p {...dataGridCellTextProps} className={'note'}>
                            {`${item?.note.substring(0, 25)}...`}
                        </p>
                        <div
                            className={classnames('part-number-text-button')}
                            onClick={() => viewFullNote(item?.note)}
                        >
                            <p className={'mb-1'}>More</p>
                            <ChevronRightRounded className='icon small' color={"primary"}/>
                        </div>
                    </div>
                    :
                    <div className={'d-flex align-items-center justify-content-center'}>
                        <IconButton
                            onPressed={() => handleUpsertingWarehouseReasonAndNote(item)}
                            disabled={submitting}
                            color={'secondary'}
                            className={'mr-2'}
                        >
                            <Edit className={"icon small"}/>
                        </IconButton>
                        <p {...dataGridCellTextProps}>{item?.note}</p>
                    </div>,
                [DataGridExoticColumnFields.detailedPanel]: !item.isSerialized
                    ? null
                    : <div
                        className={'serial-numbers-wrapper'}
                        style={{
                            width: expandableSectionWidth,
                        }}
                    >
                        {
                            item?.selectedSerials?.map(e =>
                                <span {...dataGridCellTextProps} key={e?.serialNo ?? e} className={'mr-2 mb-2'}>
                                    <Chips
                                        contentType={ChipsContentType.text}
                                        onIconClick={() => removeSerialNumber(item, e)}
                                        type={ChipsTypes.remove}
                                        content={`${e?.serialNo ?? e} (${formatMoney(e?.cost ?? item?.cost)})`}
                                    />
                                </span>
                            )
                        }
                    </div>,
            }
        }))
    }, [visibleRecords, submitting, selectedTab, expandableSectionWidth])

    /**
     * Memo version of the adjustment basic info rows info
     * @type {{header: {loadingValue: string, title: string, value: string}, body: [{id: string, title: string, value: number},{id: string, title: string, value: *},{id: string, title: string, value: string}]}}
     */
    const adjustmentBasicInfo = useMemo(() => ({
        header: {
            title: '',
            value: '',
            loadingValue: "Loading...",
        },
        body: [
            {
                id: adjustmentBasicInfoBodyIds.selectedPart,
                title: 'Products Count',
                value: allSelectedProducts.length,
            },
            {
                id: adjustmentBasicInfoBodyIds.totalPart,
                title: 'Total Product Count',
                value: allSelectedProducts.reduce((prev, cur) => {
                    return prev + cur.quantity
                }, 0),
            },
            {
                id: adjustmentBasicInfoBodyIds.type,
                title: 'Adjustment Type',
                value: selectedTab === BulkInventoryAdjustmentTabs.add ? 'Add' : 'Remove',
            },
        ],
    }), [allSelectedProducts, selectedTab])

    const showBottomBar = useMemo(() =>
            (allSelectedProducts.length > 0 && allSelectedProducts.reduce((prev, current) => prev + current.quantity, 0) > 0),
        [allSelectedProducts])

    return (
        <div className={'bulk-inventory-adjustment-view'}>
            <div className={'d-flex flex-row justify-content-between align-items-center fast-connection-view w-100 px-2 mt-3 mb-4'}>
                <p className={'view-title'}>
                    {
                        bulkInventoryAdjustmentStatus === BulkInventoryAdjustmentStatus.partSelection
                            ? 'Bulk Availability Change'
                            : 'Review'
                    }
                </p>
            </div>
            {
                bulkInventoryAdjustmentStatus === BulkInventoryAdjustmentStatus.partSelection
                    ? <>
                        <div className={'d-flex w-100'}>
                            <Tabs sx={{width: '100%'}} value={selectedTab} onChange={onTabChanged}>
                                {
                                    tabs?.map((tab) => (
                                        <Tab key={tab.value} value={tab.value} label={tab.name}/>
                                    ))
                                }
                            </Tabs>
                        </div>
                        <div className={'mb-3'}/>
                        <div className={'w-100'}>
                            <PartAutocomplete
                                clearOnBlur
                                value={null}
                                label={"Part Number"}
                                disabled={submitting}
                                onChange={(_, value) => value && onPartSelected(value)}
                                getOptionLabel={o => o?.partNumber ?? o?.partNo ?? ''}
                                textFieldProps={{
                                    variant: 'filled',
                                    InputProps: {disableUnderline: true, sx: {borderRadius: 1}},
                                }}
                            />
                        </div>
                        <div className={'mb-4'}/>
                        {allSelectedProducts.length > 0 &&
                            <>
                                <div
                                    className={'d-flex flex-row justify-content-between align-items-center fast-connection-view w-100 px-2 mt-3 mb-4'}>
                                    <p className={'text-md font-weight-semi-bold'}>
                                        Affected Products
                                    </p>
                                </div>
                                <SearchInput
                                    loading={submitting}
                                    autoSearch={true}
                                    initialValue={keywords ?? ''}
                                    search={setKeywords}
                                    label={'Search'}
                                />
                                <div className={'mb-3'}/>
                                <PartnerDataGrid
                                    {...dataGridSaveConfig}
                                    initialLayoutState={{
                                        columns: columns,
                                    }}
                                    columns={dataGridColumns}
                                    rows={dataGridRows}
                                    state={{
                                        pagination: paginationInfo,
                                        sortBy: orderBy,
                                        loading: {state: submitting},
                                        density: dataGridDensities,
                                    }}
                                    onSortByChanged={(_, current) => onSort(current)}
                                    onCurrentPageChanged={(_, current) => setPaginationInfo(current)}
                                    onPageSizeChanged={(_, current) => setPaginationInfo(current)}
                                />
                            </>
                        }
                        <div className={'spacer'}><p></p></div>
                    </>
                    : <>
                        <div className='d-flex flex-wrap w-100 justify-content-center'>
                            <Col xs={12} md={6} className={'mb-3 px-md-0 pl-md-3 pr-md-2'}>
                                <SummaryInfoCard
                                    body={adjustmentBasicInfo.body}
                                    loading={submitting}
                                    loadingCount={adjustmentBasicInfo.body.length}
                                />
                            </Col>
                            <Col xs={12} md={6} className={'mb-3  px-md-0 pr-md-3 pl-md-2'}>
                                <div className={'summary-info-card'}>
                                    <p className={'description-title'}>Selected Categories</p>
                                    <div className={'d-flex flex-wrap'}>
                                        {
                                            [...new Set(allSelectedProducts?.map(e => e.parentCategoryTitle) ?? [])].map(e => (
                                                <div
                                                    key={e}
                                                    className={'category-card'}>
                                                    <p className={'category-title'}>
                                                        {e}
                                                    </p>
                                                </div>
                                            ))
                                        }
                                    </div>
                                </div>
                            </Col>
                            <Col xs={12}>
                                <Paper className={'p-5'}>
                                    <OutlinedInput
                                        value={processAdditionalInfo}
                                        onChange={setProcessAdditionalInfo}
                                        label={'Additional Information'}
                                        placeholder={'Type here...'}
                                        multiline={true}
                                        minRows={10}
                                    />
                                </Paper>
                            </Col>
                        </div>
                    </>
            }
            {
                !!showBottomBar &&
                <PanelFooter>
                    <div className={'bottom-container'}>
                        {bulkInventoryAdjustmentStatus === BulkInventoryAdjustmentStatus.partSelection
                            ? <>
                                <div className={'d-flex '}>
                                    <div className={'d-flex mr-3'}>
                                        <p className={'title'}>Selected Products: </p>
                                        <p className={'value'}>
                                            {allSelectedProducts.length ?? 0}
                                        </p>
                                    </div>
                                    <div className={'d-flex'}>
                                        <p className={'title'}>Total Products: </p>
                                        <p className={'value'}>
                                            {allSelectedProducts.reduce((prev, current) => {
                                                return prev + current.quantity
                                            }, 0)}
                                        </p>
                                    </div>
                                </div>
                                <Button
                                    variant={'contained'}
                                    onClick={() => setBulkInventoryAdjustmentStatus(BulkInventoryAdjustmentStatus.review)}
                                    className={'px-5'}
                                >
                                    Next
                                </Button>
                            </>
                            : <>
                                <div className={'previous-step'}>
                                    <IconButton
                                        disabled={submitting}
                                        onPressed={() => setBulkInventoryAdjustmentStatus(BulkInventoryAdjustmentStatus.partSelection)}
                                        color={'paper.secondary'}
                                    >
                                        <ArrowBack className='icon small'/>
                                    </IconButton>
                                    <p>
                                        Back to part selection
                                    </p>
                                </div>
                                <Button
                                    variant={'contained'}
                                    disabled={submitting}
                                    onClick={createInventoryAdjustment}
                                    className={'px-5'}
                                >
                                    {
                                        submitting
                                            ? "Processing..."
                                            : 'Finalize'
                                    }
                                </Button>
                            </>}

                    </div>
                </PanelFooter>
            }
            <UpsertBulkAdjustmentProduct
                open={additionMoreInfoDialogOpen}
                setOpen={() => setAdditionMoreInfoDialogOpen(false) || setUpsertingWarehouseReasonNoteInitialData(null)}
                partNumber={selectedPart?.partNumber ?? selectedPart?.partNo ?? ''}
                initialData={upsertingWarehouseReasonNoteInitialData}
                handleUpsertion={
                    (values) => upsertingWarehouseReasonNoteInitialData === null
                        ? onProductAdded(values)
                        : updateProductWarehouseReasonNote(values)
                }
            />
            <ModifyProductAvailabilityDialog
                open={availabilityModificationDialogState.open}
                close={() => setAvailabilityModificationDialogState(initAvailabilityModificationDialogState)}
                warehouse={availabilityModificationDialogState.warehouse}
                serialized={true}
                increase={selectedTab === BulkInventoryAdjustmentTabs.add}
                currentAvailability={availabilityModificationDialogState.currentAvailability}
                partNo={availabilityModificationDialogState.partNo}
                handleLocalUpdate={handleUpdatingSerializedPartSerials}
                alreadySelectedSerials={availabilityModificationDialogState.selectedSerials}
                alreadyAppliedCost={availabilityModificationDialogState.cost}
            />
            <SimpleMoreDetailsDialog
                open={fullNoteDialogOpen}
                setOpen={setFullNoteDialogOpen}
                title={'Note'}
                details={fullNote}
            />
        </div>
    );
}

const UnSerializedQuantityField = ({value, handleQuantityChanged, handleFieldBlurred}) => {

    return (
        <OutlinedInput
            size={"small"}
            type={"number"}
            value={value}
            onChange={handleQuantityChanged}
            onBlur={handleFieldBlurred}
        />
    );
}

const UnSerializedCostField = ({value, handleCostChange, onFieldBlurred}) => {
    return (
        <OutlinedInput
            size={"small"}
            type={"number"}
            value={value}
            onChange={handleCostChange}
            onBlur={onFieldBlurred}
            InputProps={{
                startAdornment: <span>$</span>
            }}
        />
    )
}

export default BulkAdjustInventoryView;
